import { t } from 'i18next';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { PASSWORD_VALIDATION_LENGTH, PASSWORD_VALIDATION_SYMBOLS } from 'src/constants';
import allCountries from 'src/constants/countries.v1.json';
import { PaymentMethod } from 'src/types';
import { makeFloat } from 'react-easyrocket';
import dayjs from 'dayjs';
import { dateFormatType } from 'src/components/Inputs/InputDate';
import { getMaskPlaceholder } from 'src/components/Inputs/InputPhone/utils/getMaskPlaceholder';
import { getPhoneNumberInfo } from 'src/components/Inputs/InputPhone/utils/getPhoneNumberInfo';
import { allCountriesForReg } from 'src/constants/constants';

export function validateRequired(value: any) {
  if (!value) return t('validation.valueRequired');
  return undefined;
}

export const validateMaxLength = (maxLength: number) => (value: string) => {
  if (value && value?.length > maxLength)
    return `${t('general.valueLonger')} ${maxLength} ${t('general.characters')}`;
  return undefined;
};

export const validateDifferenceOfPasswords = (value: string, values: any) => {
  if (value === values.oldPassword) {
    return t('accountProfileSecurity.newPasswordFromOld');
  }
  return undefined;
};

export const validateNumbersString = (value: any) => {
  if (!value?.length) {
    return undefined;
  }

  if (/^[0-9]+$/.test(value)) {
    return undefined;
  }
  if (!/^[0-9]+$/.test(value)) {
    return 'registration.lettersInPhoneNumberError';
  }
};

export const validatePhoneByCountryCode = (value: string) => {
  const parsePhone = parsePhoneNumberFromString(value);
  if (!parsePhone) {
    return undefined;
  }

  const maskPlaceholder = getMaskPlaceholder(
    getPhoneNumberInfo(value, parsePhone?.country)?.country as CountryCode
  );

  if (value === maskPlaceholder) {
    return undefined;
  }

  if (getPhoneNumberInfo(value, parsePhone?.country)?.isValid) {
    return undefined;
  }
  return t('registration.invalidPhoneNumberError');
};

export const validatePositiveNumber = (value: number) => {
  if (value <= 0) return t('depositTab.invalidAmountError');
  return undefined;
};

export const validateAmountInDeposit =
  (
    min: number | undefined,
    max: number | undefined,
    currency: string,
    minPaymentMethod: string | undefined,
    maxPaymentMethod: string | undefined
  ) =>
  (value: string) => {
    const amountValue = Number(value);

    if (amountValue < Number(makeFloat(min)) || amountValue < Number(minPaymentMethod)) {
      return t('depositTab.lessThanMinimumAmountError');
    }

    if (max !== 0 && amountValue > Number(makeFloat(max))) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${makeFloat(max)}`;
    }

    if (Number(maxPaymentMethod) !== 0 && amountValue > Number(maxPaymentMethod)) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${Number(
        maxPaymentMethod
      )}`;
    }

    return undefined;
  };

export const validateAmountInWithdrawal =
  (
    min: number | undefined,
    max: number | undefined,
    currency: string,
    balance: number,
    minPaymentMethod: string | undefined,
    maxPaymentMethod: string | undefined
  ) =>
  (value: string) => {
    const amountValue = Number(value);

    if (amountValue < Number(makeFloat(min)) || amountValue < Number(minPaymentMethod)) {
      return t('depositTab.lessThanMinimumAmountError');
    }

    if (max !== 0 && amountValue > Number(makeFloat(max))) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${makeFloat(max)}`;
    }

    if (Number(maxPaymentMethod) !== 0 && amountValue > Number(maxPaymentMethod)) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${Number(
        maxPaymentMethod
      )}`;
    }

    if (amountValue > Number(makeFloat(balance))) {
      return t('accountWalletWithdrawal.notEnoughAmount');
    }
    return undefined;
  };

export const validateEmail = (email: string) => {
  if (
    email.toLowerCase().match(
      // eslint-disable-next-line no-useless-escape
      /^([^@\s\."'\(\)\[\]\{\}\\/,:;]+\.)*[^@\s\."'\(\)\[\]\{\}\\/,:;]+@[^@\s\."'\(\)\[\]\{\}\\/,:;]+(\.[^@\s\."'\(\)\[\]\{\}\\/,:;]+)+$/
    )
  ) {
    return undefined;
  }
  return t('registration.enterValidEmail');
};

export const validateCountry = (country: string | undefined) => {
  if (allCountries.find((c) => c.name === country)?.alpha3) {
    return undefined;
  }
  return t('registration.invalidCountry');
};

export const validateCountryForReg = (country: string | undefined) => {
  if (allCountriesForReg.find((c) => c.name === country)?.alpha3) {
    return undefined;
  }
  return t('registration.invalidCountry');
};

export const validateForStringEquality = (value: string, values: any) => {
  if (value !== values.newPassword) {
    return t('accountProfileSecurity.passwordsAreNotEqual');
  }
  return undefined;
};

export const validateWithdrawalAccountRequired =
  (type: string | undefined, typeToValidate: string) => (value: string) => {
    if (type !== typeToValidate) {
      if (!value) {
        return t('validation.valueRequired');
      }
      return undefined;
    }
    return undefined;
  };

export const validateWithdrawalCardRequired =
  (type: string | undefined, typeToValidate: string) => (value: string) => {
    if (type === typeToValidate) {
      if (!value) {
        return t('validation.valueRequired');
      }
      return undefined;
    }
    return undefined;
  };

export const validatePassword = (password: string) => {
  const passwordRules = [
    {
      text: `${t('rulesPassword.countSymbols')}`,
      regex: PASSWORD_VALIDATION_LENGTH
    },
    {
      text: `${t('rulesPassword.onlyLatinLetters')}`,
      regex: PASSWORD_VALIDATION_SYMBOLS
    }
  ];

  for (let i = 0; i < passwordRules.length; i++) {
    const good = passwordRules[i].regex.test(password);
    if (!good) {
      return passwordRules[i].text;
    }
  }
  return undefined;
};

export const validateTransactionValue =
  ({
    globalMin,
    globalMax,
    currency,
    selectedPaymentMethod,
    selectedCoin
  }: {
    globalMin: number | undefined;
    globalMax: number | undefined;
    currency: string;
    selectedPaymentMethod?: PaymentMethod;
    selectedCoin?: string;
  }) =>
  (value: string) => {
    const amountValue = Number(value);

    const min = Number(selectedPaymentMethod?.min_amount);
    const max = Number(selectedPaymentMethod?.max_amount);

    if (selectedCoin && selectedPaymentMethod?.type === 'coin') {
      const selectedObjectCoin = selectedPaymentMethod?.options?.find((option) =>
        option.id ? String(option.id) === selectedCoin : option.name === selectedCoin
      );

      const exchangeRate = Number(selectedObjectCoin?.exchangeRate);

      const minCoin = min / exchangeRate;
      const maxCoin = max / exchangeRate;

      if (amountValue < Number(makeFloat(globalMin)) || amountValue < minCoin) {
        return t('depositTab.lessThanMinimumAmountError');
      }

      if (globalMax !== undefined && maxCoin !== 0 && amountValue > maxCoin) {
        return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${maxCoin.toFixed(2)}`;
      }

      if (max !== 0 && amountValue > max / exchangeRate) {
        return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${maxCoin.toFixed(2)}`;
      }

      return undefined;
    }

    if (amountValue < Number(makeFloat(globalMin)) || amountValue < min) {
      return t('depositTab.lessThanMinimumAmountError');
    }

    if (globalMax !== undefined && globalMax !== 0 && amountValue > Number(makeFloat(globalMax))) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${makeFloat(globalMax)}`;
    }

    if (max !== 0 && amountValue > max) {
      return `${t('depositTab.moreThenMaximumAmountError')} ${currency} ${max}`;
    }

    return undefined;
  };

export const compareWithBalance = (balance?: number) => (value: string) => {
  if (balance === undefined) return undefined;
  const amountValue = Number(value);

  if (amountValue > Number(makeFloat(balance))) {
    return t('accountWalletWithdrawal.notEnoughAmount');
  }

  return undefined;
};

export const validateDateOfBirthSingleField = (values: string, dateFormat?: dateFormatType) => {
  switch (dateFormat) {
    case 'DD-MM-YYYY': {
      const [day, month, year] = values.split('-');
      if (!day || !month || !year) {
        return undefined;
      }
      return validateDateOfBirth(day, month, year);
    }
    case 'MM-DD-YYYY': {
      const [month, day, year] = values.split('-');
      if (!day || !month || !year) {
        return undefined;
      }
      return validateDateOfBirth(day, month, year);
    }
    case 'YYYY-MM-DD': {
      const [year, month, day] = values.split('-');
      if (!day || !month || !year) {
        return undefined;
      }
      return validateDateOfBirth(day, month, year);
    }
    default: {
      const [day, month, year] = values.split('-');
      if (!day || !month || !year) {
        return undefined;
      }
      return validateDateOfBirth(day, month, year);
    }
  }
};

export const validateDateOfBirthThreeFields = (value: string | undefined, values: any) => {
  const { day, month, year } = values;
  if (!day || !month || !year) {
    return undefined;
  }
  return validateDateOfBirth(day, month, year);
};

function validateDateOfBirth(day: string, month: string, year: string) {
  const enteredDate = dayjs()
    .date(parseInt(day))
    .month(parseInt(month) - 1)
    .year(parseInt(year));

  if (enteredDate.daysInMonth() < Number(day)) {
    return t('apiErrors.invalidDayFormat');
  }

  if (enteredDate.add(18, 'years').valueOf() > dayjs().valueOf()) {
    return t('apiErrors.mustBeOldest');
  }

  return undefined;
}
